import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Center, Code, Divider, Image, PasswordInput, PinInput, Stack, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  confirmTotp, enrolTotp, googleLogin, googleSignInConfig, login, resolveApiUrl, verifyTotp,
  type LoginResponse, type TotpEnrolment,
} from '../api/auth';
import { useAuth } from './AuthProvider';
import { GoogleButton } from './GoogleButton';

export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  /* Set when the account has to enrol in two-factor before it can sign in at
     all, which is a different screen from being asked for a code. */
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Set when the gateway offers Google sign-in; the button needs its client ID.
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  // Remembered across the two-factor step, so the notice still shows after it.
  const [passwordDisabled, setPasswordDisabled] = useState(false);

  useEffect(() => {
    // Not in the desktop app. Google refuses sign-in inside embedded app
    // windows like Tauri's, and the page there is not served from a web
    // address Google would accept, so the button could only fail.
    if ('__TAURI_INTERNALS__' in window) return;
    let live = true;
    googleSignInConfig()
      .then((c) => live && setGoogleClientId(c.client_id ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  function complete(res: LoginResponse) {
    if (!res.token || !res.user) {
      setError(res.error ?? 'Login failed');
      return;
    }
    if (passwordDisabled || res.password_disabled) {
      notifications.show({
        color: 'orange',
        autoClose: false,
        title: 'Your password has been switched off',
        message:
          'This account was set up without its email being confirmed, so its password could have ' +
          'been chosen by someone else. Sign in with Google from now on, or ask your admin to set you ' +
          'a new password.',
      });
    }
    signIn(res.token, resolveApiUrl(res), res.user);
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    await run(() => login(email, password));
  }

  async function submitGoogle(credential: string) {
    await run(() => googleLogin(credential));
  }

  /**
   * Password and Google sign-in answer in the same shape, so both go through
   * here: a refusal, a two-factor challenge, enrolment, or signed in.
   */
  async function run(attempt: () => Promise<LoginResponse>) {
    setBusy(true);
    setError('');
    try {
      const res = await attempt();
      if (res.password_disabled) setPasswordDisabled(true);
      if (!res.success) {
        setError(res.error ?? 'Login failed');
      } else if (res.requires_totp && res.mfa_token) {
        // 2FA is enforced by default on the gateway. Mobile shipped without
        // this step and every fresh install failed with "Login failed".
        setMfaToken(res.mfa_token);
      } else if (res.requires_totp_setup && res.mfa_token) {
        // Their organisation requires two-factor and this account has none, so
        // they have to enrol before they can get in. Checking only
        // `requires_totp` sent this case down the signed-in branch, where it
        // found no token and reported "Login failed" with nothing to act on.
        setMfaToken(res.mfa_token);
        setEnrolment(await enrolTotp(res.mfa_token));
      } else {
        complete(res);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  function backToPassword() {
    setPasswordDisabled(false);
    setMfaToken(null);
    setEnrolment(null);
    setCode('');
    setError('');
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setBusy(true);
    setError('');
    try {
      // Enrolling and verifying take the same code from the same app, but
      // different endpoints: one saves the new secret, the other checks
      // against the saved one.
      const res = enrolment
        ? await confirmTotp(mfaToken, code)
        : await verifyTotp(mfaToken, code);
      if (!res.success) setError(res.error ?? 'That code was not accepted');
      else complete(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Center mih="100vh" p="md">
      <Card w={400} shadow="sm">
        <Stack>
          {/* The Rowstorm wordmark. `rowstorm-wordmark.svg` is a white
              knockout asset (the same mark the dark sidebar uses), so it needs
              brightness(0) to read on a pale surface rather than shipping a
              second file. A text heading here read as a placeholder next to it. */}
          <div>
            <img
              src="/rowstorm-wordmark.svg"
              alt="Rowstorm"
              style={{
                width: 150,
                height: 'auto',
                display: 'block',
                margin: '0 auto 1.25rem',
                filter: 'brightness(0)',
                opacity: 0.9,
              }}
            />
            {/* Only when there is something to say. The page used to carry a
                tagline here; the logo already says whose sign-in this is. */}
            {mfaToken && (
              <Text
                ta="center"
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--mantine-color-dimmed)',
                  lineHeight: 1.5,
                  letterSpacing: '0.01em',
                }}
              >
                {enrolment
                  ? 'Your organisation requires two-factor authentication'
                  : 'Enter your authenticator code'}
              </Text>
            )}
          </div>

          {error && <Alert color="red" variant="light">{error}</Alert>}

          {mfaToken ? (
            <form onSubmit={submitCode}>
              <Stack>
                {enrolment && (
                  <Stack gap="xs" align="center">
                    <Text size="sm" c="dimmed" ta="center">
                      Scan this with an authenticator app, then enter the code it shows.
                    </Text>
                    {enrolment.qr && <Image src={enrolment.qr} w={180} h={180} alt="Setup code" />}
                    {enrolment.secret && (
                      <>
                        <Text size="xs" c="dimmed">Or type this in by hand:</Text>
                        <Code>{enrolment.secret}</Code>
                      </>
                    )}
                  </Stack>
                )}
                <Center>
                  <PinInput length={6} type="number" oneTimeCode value={code} onChange={setCode} />
                </Center>
                <Button type="submit" loading={busy} disabled={code.length < 6} fullWidth>
                  {enrolment ? 'Turn on two-factor and sign in' : 'Verify'}
                </Button>
                <Button variant="subtle" size="compact-sm" onClick={backToPassword}>
                  Back
                </Button>
              </Stack>
            </form>
          ) : (
            <Stack>
            {googleClientId && (
              <>
                <GoogleButton
                  clientId={googleClientId}
                  width={352}
                  onCredential={submitGoogle}
                  onError={setError}
                />
                <Divider label="or sign in with email" labelPosition="center" />
              </>
            )}
            <form onSubmit={submitPassword}>
              <Stack>
                <TextInput
                  label="Email" type="email" required autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.currentTarget.value)}
                />
                <PasswordInput
                  label="Password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.currentTarget.value)}
                />
                <Button type="submit" loading={busy} fullWidth>Sign in</Button>
              </Stack>
            </form>
            </Stack>
          )}
        </Stack>
      </Card>
    </Center>
  );
}
